import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth';
import { StateStoreService } from '../services/state-store/state-store';
import { isSafeReturnUrl } from '../utils/return-url.utils';
import {
  PAYMENT_METHOD_RETURN_CANCELED,
  PAYMENT_METHOD_RETURN_PARAM,
  PAYMENT_METHOD_RETURN_SAVED,
} from '../utils/billing-readiness.utils';

/**
 * Gates `/complete-profile`. The companion to `profileCompletionGuard`:
 *   - anonymous → bounce to `/login`,
 *   - returning from Stripe's card-setup page → allow (see below),
 *   - already-complete profile → bounce to `returnUrl` (or `/organisation`),
 *   - completion actually needed → allow the route.
 *
 * Readiness is guaranteed by `appReadyMatch` at the route level.
 *
 * `returnUrl` handling mirrors the component-side `resolvePostSignupTarget`:
 *   - `parseUrl` rather than `createUrlTree([url])`: the returnUrl may
 *     carry a query string (`/edit-document?documentId=…`), which the
 *     array form percent-encodes into a single broken segment.
 *   - `isSafeReturnUrl` is the open-redirect defence (see
 *     `utils/return-url.utils.ts`) — without it, a crafted
 *     `?returnUrl=https://evil.example.com` bounces the user off-app.
 */
export const profileCompletionInitGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const auth = inject(AuthService);
  const state = inject(StateStoreService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) return router.createUrlTree(['/login']);

  // Coming back from Stripe's hosted card-setup page (`return_to:
  // 'onboarding'`). `needsProfileCompletion` is false by this point —
  // terms, name and org are all satisfied, and billing readiness is not
  // one of its inputs — so the ordinary check below would bounce the
  // customer before the wizard could resume: the poll for the attached
  // card would never run, and any step they still owe (an address they
  // skipped) would be silently dropped.
  //
  // Safe to let through on the param alone. The param is a signal to
  // re-resolve, not a claim of state: the component recomputes its steps
  // from `GET .../payment-method` and navigates straight back out when
  // nothing is left to do, so a hand-typed URL buys nothing.
  if (isReturningFromCardSetup(route)) return true;

  const hasOrg = !!state.selectedOrgId();
  // Same computed `profileCompletionGuard` and the component's
  // step-builder read. Mirroring it exactly is what stops the two guards
  // ping-ponging: if that one redirects here for a billing blocker and
  // this one bounced back on a narrower test, the browser would hang.
  if (!auth.needsProfileCompletion(hasOrg, state.requiresBillingDetails())) {
    const returnUrl = route.queryParams['returnUrl'];
    const target = isSafeReturnUrl(returnUrl) ? returnUrl : '/organisation';
    return router.parseUrl(target);
  }

  return true;
};

function isReturningFromCardSetup(route: ActivatedRouteSnapshot): boolean {
  const value = route.queryParams[PAYMENT_METHOD_RETURN_PARAM];
  return value === PAYMENT_METHOD_RETURN_SAVED || value === PAYMENT_METHOD_RETURN_CANCELED;
}
